import React, { useEffect, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import { ukUA } from "@material-ui/core/locale";
import {
	deleteUserInBase,
	getAllUsersFromDB,
} from "../../store/actions/usersAction";
import { setDashboardCertificateState } from "admin/store/actions/logsActions";
import Page from "../../components/Page";
import { TableDeleteButton, TableItemButtons } from "../../components/Table";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { TextField } from "@material-ui/core";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import {DataGrid, GridToolbar, GridToolbarDensitySelector, GridToolbarFilterButton} from "@mui/x-data-grid";
import {UserSortField} from "./userSortFields";
import IconButton from "@material-ui/core/IconButton";
import PropTypes from "prop-types";

const theme = createTheme(
	{
		palette: {
			primary: { main: "#1976d2" },
		},
	},
	ukUA
);

function QuickSearchToolbar(props) {
	return (
		<div >
			<TextField
				variant="standard"
				value={props.value}
				onChange={props.onChange}
				placeholder="Search…"
				InputProps={{
					startAdornment: <SearchIcon fontSize="small" />,
					endAdornment: (
						<IconButton
							title="Clear"
							aria-label="Clear"
							size="small"
							style={{ visibility: props.value ? "visible" : "hidden" }}
							onClick={props.clearSearch}
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					),
				}}
			/>
		</div>
	);
}

QuickSearchToolbar.propTypes = {
	clearSearch: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	value: PropTypes.string.isRequired,
};

function escapeRegExp(value) {
	return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

export default function UserTable() {
	const dispatch = useDispatch();
	const [users, setUsers] = useState([]);
	const [allUsers, setAllUsers] = useState([]);
	const { logs } = useSelector((state) => state.admin);

	const [value, setValue] = useState("");
	const [deleteUser, setDeleteUser] = useState({});
	const [showConfirm, setShowConfirm] = useState(false);
	const [sortFields, setSortFields] = useState([
		UserSortField.registrationDate,
		UserSortField.firstName,
		UserSortField.lastName,
	]);

	useEffect(() => {
		//const us = getAllUsersFromDateBase(page, name, lastName, city, email);
		dispatch(setDashboardCertificateState());
		getAllUsersFromDB().then((res) => {
			setAllUsers(res);
			setUsers(res);
		});
	}, []);

	const deleteUserAction = () => {
		dispatch(deleteUserInBase(deleteUser)).then(() => {
			setShowConfirm(false);
			setDeleteUser({});
			getAllUsersFromDB().then((res) => {
				setAllUsers(res);
				setUsers(res);
			});
		});
	};

	const confirmDeleteUser = (e, user) => {
		e.stopPropagation();
		e.nativeEvent.stopImmediatePropagation();
		setShowConfirm(true);
		setDeleteUser(user);
	};

	const HasSertificate = (id) => {
		if (users !== undefined && users?.length != undefined && users?.length != 0) {
			let checkSert = false;
			let checkedUser = users?.find((u) => u.id === id);
			if (logs?.dashboard && logs?.dashboard[checkedUser?.email]) {
				checkSert = true;
			}
			return checkSert;
		}
	};

	const [searchText, setSearchText] = React.useState("");

	const requestSearch = (searchValue) => {
		setSearchText(searchValue);
		const searchRegex = new RegExp(escapeRegExp(searchValue), "i");
		const filteredRows = allUsers.filter((row) => {
			return Object.keys(row).some((field) => {
				return searchRegex.test(row[field].toString());
			});
		});
		setUsers(filteredRows);
	};

	React.useEffect(() => {
		setUsers(users);
	}, [users]);

	const columns = [
		{ field: "name", headerName: "Ім'я", width: 150 },
		{ field: "lastName", headerName: "Прізвище", width: 150 },
		{
			field: "city",
			valueGetter: (params) => `${params.value.label}`,
			headerName: "Населений пункт",
			width: 200,
		},
		{ field: "email", headerName: "Email", width: 200 },
		{ field: "registrationDate", headerName: "Дата реєстрації", width: 200,
			// eslint-disable-next-line react/display-name
			renderCell: cellValues => {
				if (cellValues.value) {
					const date = new Date(cellValues.value);
					return (<div>{moment(date).format("DD/MM/YYYY")}</div>);
				}

				return <></>;
			}
		},
		{
			field: "id",
			headerName: "Дії",
			flex: 100,
			sortable: false,
			// eslint-disable-next-line react/display-name
			renderCell: (cellValues) => {
				return (
					<div
						style={{ width: "100%", display: "flex", flexFlow: "row" }}
						key={cellValues.value}>
						<div style={{ float: "right", width: "100%" }}>
							{users && HasSertificate(cellValues.value) && (
								<Alert severity="success" style={{ width: 220 }}>
									Сертифікат отриманий
								</Alert>
							)}
						</div>
						<div style={{ float: "left" }}>
							<TableItemButtons>
								<Button
									style={{
										fontSize: "0.8rem",
										padding: "6px 12px",
										marginLeft: "10px",
										textDecoration: "none",
									}}
									variant="contained"
									color="primary">
									<Link
										style={{ textDecoration: "none" }}
										to={`/admin/user/${cellValues.value}/edit`}>
										Змінити
									</Link>
								</Button>
								<TableDeleteButton
									onClick={(e) => confirmDeleteUser(e, cellValues.value)}
								/>
							</TableItemButtons>
						</div>
					</div>
				);
			},
		},
	];

	return (
		<ThemeProvider theme={theme}>
			<Page title="Користувачі">
				<DataGrid
					rows={users}
					columns={columns}
					autoPageSize
					pagination
					aria-label="Користувачі"
					disableColumnMenu={true}
					sortModel={sortFields}
					sortingOrder={["asc", "desc"]}
					components={{
						Toolbar: QuickSearchToolbar,
					}}
					componentsProps={{
						toolbar: {
							value: searchText,
							onChange: (event) => requestSearch(event.target.value),
							clearSearch: () => requestSearch(""),
						},
					}}
					onSortModelChange={(newModel) => {
						let wasSortChanged = false;

						if (newModel.length === sortFields.length) {
							newModel.forEach((m, index) => {
								if (m.sort !== sortFields[index].sort || m.field !== sortFields[index].field) {
									wasSortChanged = true;
									return;
								}
							});
						} else {
							wasSortChanged = true;
						}

						if (wasSortChanged) {
							setSortFields(newModel);
						}
					}}></DataGrid>
				{showConfirm && (
					<Dialog
						open={showConfirm}
						onClose={() => setShowConfirm(false)}
						aria-labelledby="alert-dialog-title"
						aria-describedby="alert-dialog-description">
						<DialogTitle id="alert-dialog-title">
							Ви впевнені, що бажаєте видалити користувача?
						</DialogTitle>
						<DialogContent>
							<DialogContentText id="alert-dialog-description">
								Після видалення користувача відновити його буде не можливо
							</DialogContentText>
							<DialogContentText>
								Для підтвердження введіть <strong>ВИДАЛИТИ</strong>:
							</DialogContentText>
							<TextField
								fullWidth
								autoFocus
								margin="dense"
								id="name"
								value={value}
								variant="standard"
								onInput={(e) => {
									e.stopPropagation();
									e.nativeEvent.stopImmediatePropagation();
									setValue(e.target.value);
								}}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setShowConfirm(false)} color="primary">
								Скасувати
							</Button>
							<Button
								disabled={value !== "ВИДАЛИТИ"}
								onClick={deleteUserAction}
								color="primary"
								autoFocus>
								Підтвердити
							</Button>
						</DialogActions>
					</Dialog>
				)}
			</Page>
		</ThemeProvider>
	);
}
